import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useProjects } from '../../hooks/useProjects';
import { useImportEntries } from '../../hooks/useTimesheets';
import type { ImportResult } from '../../services/api/timeEntry';

interface ImportEntriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ImportEntriesModal({ isOpen, onClose }: ImportEntriesModalProps) {
  const [projectId, setProjectId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const mutation = useImportEntries();
  const { data: projects = [] } = useProjects({ state: 'in_progress' });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProjectId('');
      setFile(null);
      setResult(null);
      mutation.reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePreview = () => {
    if (!projectId || !file) return;
    mutation.mutate(
      { projectId: parseInt(projectId, 10), file, dryRun: true },
      { onSuccess: (response) => setResult(response.data) }
    );
  };

  const handleImport = () => {
    if (!projectId || !file) return;
    mutation.mutate(
      { projectId: parseInt(projectId, 10), file, dryRun: false },
      { onSuccess: (response) => setResult(response.data) }
    );
  };

  const hasProblems = result &&
    (result.problems.userNotFound.length > 0 || result.problems.entryNotEditable.length > 0);

  const canImport = result?.dryRun && result.imported > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Entries from Tempo"
      size="md"
      footer={
        result && !result.dryRun ? (
          <div className="flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : result?.dryRun ? (
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!canImport}
              isLoading={mutation.isPending}
            >
              Import
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handlePreview}
              disabled={!projectId || !file}
              isLoading={mutation.isPending}
            >
              Preview
            </Button>
          </div>
        )
      }
    >
      {result ? (
        <div className="space-y-4">
          <div className={`${result.dryRun ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} border rounded-lg p-3`}>
            <p className={`text-sm font-medium ${result.dryRun ? 'text-blue-800' : 'text-green-800'}`}>
              {result.dryRun ? (
                <><strong>{result.imported}</strong> entries will be imported.</>
              ) : (
                <>Successfully imported <strong>{result.imported}</strong> entries.</>
              )}
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Breakdown:</p>
            <dl className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <dt>Rows in file</dt>
                <dd className="font-medium text-gray-900">{result.totalRows}</dd>
              </div>
              {result.rowsSkipped > 0 && (
                <div className="flex justify-between">
                  <dt>Rows skipped (bad data)</dt>
                  <dd className="font-medium text-amber-600">{result.rowsSkipped}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt>Unique entries (user + date)</dt>
                <dd className="font-medium text-gray-900">{result.aggregatedEntries}</dd>
              </div>
              {result.zeroHoursSkipped > 0 && (
                <div className="flex justify-between">
                  <dt>Skipped (zero hours)</dt>
                  <dd className="font-medium text-amber-600">{result.zeroHoursSkipped}</dd>
                </div>
              )}
              {result.problems.userNotFound.length > 0 && (
                <div className="flex justify-between">
                  <dt>Skipped (user not found)</dt>
                  <dd className="font-medium text-amber-600">
                    {result.problems.userNotFound.length} user{result.problems.userNotFound.length !== 1 ? 's' : ''}
                  </dd>
                </div>
              )}
              {result.problems.entryNotEditable.length > 0 && (
                <div className="flex justify-between">
                  <dt>Skipped (already submitted/approved)</dt>
                  <dd className="font-medium text-amber-600">{result.problems.entryNotEditable.length}</dd>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                <dt className="font-medium text-gray-900">{result.dryRun ? 'To import' : 'Imported'}</dt>
                <dd className="font-bold text-gray-900">{result.imported}</dd>
              </div>
            </dl>
          </div>
          {hasProblems && (
            <div className="space-y-3">
              {result.problems.userNotFound.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-yellow-800 mb-1">Users not found:</p>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {result.problems.userNotFound.map((id) => (
                      <li key={id}>{id}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.problems.entryNotEditable.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-yellow-800 mb-1">Skipped — already submitted or approved:</p>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {result.problems.entryNotEditable.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {mutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'Import failed. Please try again.'}
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">Select a project...</option>
              {[...projects]
                .sort((a, b) => (a.fullName || a.name).localeCompare(b.fullName || b.name))
                .map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.fullName || project.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tempo export file (.xlsx)
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
